import React, { useState } from 'react';
import { SponsorForm } from './SponsorForm';
import { MessageTemplateForm } from './MessageTemplateForm';
import { useSponsor } from '../../contexts/SponsorContext';
import { verifyStorageBucket } from '../../lib/supabase/index';
import { MessageSquare, Plus, Trash2, Edit2 } from 'lucide-react';

interface SponsorManagementProps {
  tournamentId: string;
  isAdmin?: boolean;
  selectedTournament?: any;
  onTournamentUpdate?: (tournament: any) => void;
}

export function SponsorManagement({ 
  tournamentId,
  isAdmin = false,
  selectedTournament,
  onTournamentUpdate = () => {}
}: SponsorManagementProps) {
  const {
    currentSponsor,
    messageTemplates,
    isLoading,
    error,
    updateSponsor,
    createMessageTemplate,
    updateMessageTemplate,
    deleteMessageTemplate
  } = useSponsor();

  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);

  const handleAddSponsor = async () => {
    try {
      if (!selectedTournament) {
        throw new Error('No tournament selected');
      }

      // Verify storage bucket first
      await verifyStorageBucket();
       
      // Create test sponsor data
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      
      const testSponsor = {
        tournament_id: tournamentId,
        sponsor_metadata: {
          name: 'Test Sponsor',
          logo_url: 'https://placehold.co/400x100/075985/FFFFFF.png?text=Test+Sponsor',
          website: 'https://example.com',
          description: 'This is a test sponsor'
        },
        start_time: now.toISOString(),
        end_time: tomorrow.toISOString(),
        active: true
      };

      // Update tournament metadata first
      const updatedTournament = {
        ...selectedTournament,
        metadata: {
          ...selectedTournament.metadata,
          sponsor: {
            ...testSponsor.sponsor_metadata,
            start_time: testSponsor.start_time,
            end_time: testSponsor.end_time,
            active: testSponsor.active
          }
        }
      };

      // Update tournament first to ensure metadata is saved
      await onTournamentUpdate(updatedTournament);

      // Then update sponsor
      await updateSponsor(testSponsor);
       
      console.log('Test sponsor added successfully');
    } catch (error) {
      console.error('Failed to add test sponsor:', error);
    }
  };

  const handleSponsorSubmit = async (data: any) => {
    try {
      if (!selectedTournament) {
        throw new Error('No tournament selected');
      }

      // Update tournament metadata first
      const updatedTournament = {
        ...selectedTournament,
        metadata: {
          ...selectedTournament.metadata,
          sponsor: {
            ...data.metadata,
            start_time: data.start_time,
            end_time: data.end_time,
            message_template_id: data.message_template_id,
            active: data.active
          }
        }
      };

      // Update tournament first
      await onTournamentUpdate(updatedTournament);

      // Then update sponsor
      await updateSponsor({
        tournament_id: tournamentId,
        sponsor_metadata: {
          name: data.metadata.name,
          logo_url: data.metadata.logo_url,
          website: data.metadata.website,
          description: data.metadata.description
        },
        start_time: data.start_time,
        end_time: data.end_time,
        message_template_id: data.message_template_id,
        active: data.active
      });
    } catch (error) {
      console.error('Failed to update sponsor:', error);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading sponsor data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Sponsor Form */}
      <div className="bg-white p-6">
        <h3 className="text-lg font-semibold mb-6">Tournament Sponsor</h3>
        <SponsorForm
          initialSponsor={currentSponsor ? {
            metadata: currentSponsor.sponsor_metadata,
            start_time: currentSponsor.start_time,
            end_time: currentSponsor.end_time,
            message_template_id: currentSponsor.message_template_id,
            active: currentSponsor.active
          } : undefined}
          messageTemplates={messageTemplates}
          onSubmit={(data) => {
            handleSponsorSubmit(data);
          }}
        />
        
        {/* Test Sponsor Section */}
        {isAdmin && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-4">Test Sponsor</h4>
            <div className="space-y-4">
              <button
                onClick={() => {
                  const now = new Date();
                  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
                  handleSponsorSubmit({
                    metadata: {
                      name: 'Test Sponsor',
                      logo_url: 'https://placehold.co/400x100/075985/FFFFFF.png?text=Test+Sponsor',
                      website: 'https://example.com',
                      description: 'This is a test sponsor'
                    },
                    start_time: now.toISOString(),
                    end_time: tomorrow.toISOString(),
                    active: true
                  });
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Test Sponsor
              </button>
              {currentSponsor && (
                <button
                  onClick={() => {
                    handleSponsorSubmit({
                      ...currentSponsor,
                      active: false
                    });
                  }}
                  className="w-full px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove Test Sponsor
                </button>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Test sponsors are useful for previewing sponsor functionality during tournament setup.
            </p>
          </div>
        )}
      </div>

      {/* Message Templates */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">Message Templates</h3>
          <button
            onClick={() => setShowTemplateForm(true)}
            className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Template
          </button>
        </div>

        {showTemplateForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <MessageTemplateForm
              onSubmit={async (data) => {
                await createMessageTemplate(data);
                setShowTemplateForm(false);
              }}
            />
          </div>
        )}

        <div className="space-y-4">
          {messageTemplates.map(template => (
            <div
              key={template.id}
              className={`p-4 rounded-lg border ${
                template.active ? 'border-gray-200' : 'border-gray-100 opacity-60'
              }`}
            >
              {editingTemplate === template.id ? (
                <MessageTemplateForm
                  initialTemplate={{
                    message: template.message,
                    interval_minutes: template.interval_minutes,
                    active: template.active
                  }}
                  onSubmit={async (data) => {
                    await updateMessageTemplate({
                      ...template,
                      ...data
                    });
                    setEditingTemplate(null);
                  }}
                />
              ) : (
                <div>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                      <MessageSquare className="w-4 h-4" />
                      Every {template.interval_minutes} minutes
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingTemplate(template.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteMessageTemplate(template.id)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-900 whitespace-pre-wrap">{template.message}</p>
                </div>
              )}
            </div>
          ))}

          {messageTemplates.length === 0 && !showTemplateForm && (
            <div className="text-center py-8 text-gray-500">
              No message templates yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
}